import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

// App-level error boundary. A render error anywhere in the routed tree used to
// unmount everything into a dead white screen (and the user couldn't even
// navigate to log in). This catches the error, keeps the chrome usable, and
// offers a way out. It auto-resets when the route changes.

const Wrap = styled.div`
  min-height: 50vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 40px 20px;
  text-align: center;
`;

const Title = styled.h1`
  font-family: 'Fjalla One', sans-serif;
  font-size: 22px;
  color: #fff;
  text-transform: uppercase;
  margin: 0;
`;

const Msg = styled.p`
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 15px;
  color: rgba(255, 255, 255, 0.6);
  max-width: 340px;
  line-height: 1.5;
  margin: 0;
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
`;

const Btn = styled.button`
  padding: 10px 18px;
  border-radius: 8px;
  border: 1px solid rgba(0, 213, 255, 0.5);
  background: rgba(0, 213, 255, 0.14);
  color: rgb(0, 213, 255);
  font-family: 'Fjalla One', sans-serif;
  font-size: 14px;
  text-transform: uppercase;
  cursor: pointer;

  &:hover { background: rgba(0, 213, 255, 0.24); }
`;

interface FallbackProps {
  onReset: () => void;
}

const Fallback: React.FC<FallbackProps> = ({ onReset }) => {
  const navigate = useNavigate();
  return (
    <Wrap role="alert">
      <div style={{ fontSize: 40 }} aria-hidden="true">🎲</div>
      <Title>Something went wrong</Title>
      <Msg>
        This screen hit an unexpected error. Your data is safe — try again, or head back home.
      </Msg>
      <Row>
        <Btn onClick={() => { onReset(); navigate('/'); }}>Go home</Btn>
        <Btn onClick={() => window.location.reload()}>Reload</Btn>
      </Row>
    </Wrap>
  );
};

interface Props {
  children: React.ReactNode;
  /** When this value changes, the boundary clears its error (e.g. on route change). */
  resetKey?: string;
}

interface State {
  hasError: boolean;
}

class ErrorBoundaryInner extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidUpdate(prev: Props) {
    // Route changed after an error → clear it so the new page can render.
    if (this.state.hasError && prev.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Keep a console trail for debugging; no external reporting.
    console.error('ErrorBoundary caught:', error, info?.componentStack);
  }

  reset = () => this.setState({ hasError: false });

  render() {
    if (this.state.hasError) {
      return <Fallback onReset={this.reset} />;
    }
    return this.props.children;
  }
}

/** Wraps children and resets automatically whenever the route path changes. */
const ErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  return <ErrorBoundaryInner resetKey={location.pathname}>{children}</ErrorBoundaryInner>;
};

export default ErrorBoundary;
